import { useConnectIntegrationStore } from "#features/conversations/stores/useConnectIntegrationStore.ts";
import { usePersonalIntegrationsUrl } from "#hooks/usePersonalIntegrationsUrl.ts";
import { AiQueryKeys } from "#lib/AiQueryKeys.ts";
import type { ConnectIntegrationRequest } from "#types/ConnectIntegration.ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * Runs a connect banner's action through the host's connection flow when one is registered, and
 * otherwise falls back to opening the management app in a new tab — the behavior for hosts that
 * embed the assistant without owning a connection flow.
 *
 * The host reports success and failure itself, so the banners show no state of their own; the
 * assistant only refreshes the connection status once the user is connected.
 */
export const useConnectIntegration = () => {
    const connectIntegrationHandler = useConnectIntegrationStore(x => x.connectIntegrationHandler);
    const personalIntegrationsUrl = usePersonalIntegrationsUrl();
    const queryClient = useQueryClient();

    return useCallback((request: ConnectIntegrationRequest) => {
        if (!connectIntegrationHandler) {
            window.open(personalIntegrationsUrl, "_blank", "noopener,noreferrer");

            return;
        }

        connectIntegrationHandler(request, {
            onSuccess: () => {
                // Flips the banner to Connected without a reload. The agent picks the connection up
                // on its own, since the next turn resolves the user's tools server-side.
                void queryClient.invalidateQueries({ queryKey: AiQueryKeys.GetChannelsDetails() });
            }
        });
    }, [connectIntegrationHandler, personalIntegrationsUrl, queryClient]);
};
